import React, { FunctionComponent } from "react";
import { Typography, Link, Box, Tooltip } from "@mui/material";
import { useHistory, useLocation } from "react-router";
import moment from "moment";
import ReactToPrint from "react-to-print";
import { useDispatch, useSelector } from "react-redux";
import Barcode from "react-barcode";
import "../PrintBill.scss";
import { convertADtoBS } from "../../../../components/Calendar/functions/calendarFunctions";
import { t, tl } from "../../../../components/translate";
import { registerPrint } from "../../../../actions/bill";
import { DocumentTypes, BillType } from "../../../../interfaces/BillInterfaces";
import { dateWithZeroBeforeSingleDigits } from "../../../../helpers/formatters";
import { isOldFormatBill } from "../../../../actions/helpers/billHelper";
import DescribeBillV1 from "../BillDescriptionV1";
import usePrintStyles from "../../../../hooks/usePrintStyles";
import { checkWalkInCustomer, scaleFont } from "../BillPrintHelpers";
import { RootState } from "../../../../store";
import usePrintShortcut from "../../../../hooks/usePrintShortcut";
import logo from "../../../../../assets/images/poweredByOkhati.png";
import PrintHeader from "./PrintHeader";
import InfoField, { Size } from "./InfoField";
import ClientInfo from "./ClientInfo";
import { Client } from "../../../../interfaces/ClientInterface";
import BillDescription from "./BillDescription";
import useLabIdsFromBills from "../../../../hooks/useLabIdsFromBills";
import QrFromUrl from "../../../../components/Qr/QrFromUrl";
import { hasValidRichTextRemark } from "../../Editor/BilllEditorActions";
import { DataComment } from "../../../Lab/LabPrint/Templates/DefaultTemplate/DefaultTemplateCore";
import { getPrintTitle } from "../PrintBill";
import { rupeeDisplay } from "../../../../helpers/rupee";

interface PrintBillProps {
  buttonText?: string;
  billId: number;
  billData: BillType;
  customerNumber: string | null;
  labelStyle?: React.CSSProperties;
  addPrintCount?: boolean;
  onPrintClick?: () => void;
  showDobOnPrint: boolean;
  hidePaymentMethodOnPrint?: boolean;
  isForPreview?: boolean;
  labTestUrl?: string;
  printAsEstimate?: boolean;
  clientBalance?: number;
}

const EightyMmPrint: FunctionComponent<PrintBillProps> = ({
  billId,
  billData,
  buttonText = "Print",
  labelStyle = {},
  addPrintCount = true,
  onPrintClick,
  showDobOnPrint = false,
  hidePaymentMethodOnPrint = false,
  isForPreview = false,
  labTestUrl,
  printAsEstimate = false,
  clientBalance = 0
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const labIdsForBill = useLabIdsFromBills(billData);
  const location = useLocation<{ openPrintWindow?: boolean }>();

  // refs
  const printRef = React.useRef<HTMLDivElement>(null);
  const reactToPrintRef = React.useRef<HTMLAnchorElement>(null);

  // selectors
  const { resourceCentres } = useSelector((state: RootState) => state.resources);
  const rcId = useSelector((state: RootState) => state.userContext.resourceCentreId);
  const SPs = useSelector((state: RootState) => state.resources.resourceCentreServiceProviders);

  // variables
  const resourceCentre = resourceCentres.find((rc) => rc.id === rcId);
  const showDueOnBillPrint = localStorage.getItem("showDueOnBillPrint") === "true";
  const customTitle = resourceCentre?.settings?.printSettings?.billPrintSettings?.customTitle;
  const billDue = (billData.summary?.totalAmount || 0) - (billData.paymentInfo.paidAmount || 0);
  const showLetterhead =
    resourceCentre?.settings?.printSettings.billPrintSettings?.includeLetterhead;

  const defaultPrintStyles = usePrintStyles({
    pageStyle: `size: 80mm auto; margin: 0; margin-bottom: 12pt;`
  });

  React.useEffect(() => {
    if (
      location.state?.openPrintWindow &&
      (!billData?.document?.createdLabTestIds?.length ||
        (billData?.document?.createdLabTestIds?.length && Boolean(labTestUrl)))
    ) {
      defaultPrintStyles.applyPrintStyles();
      reactToPrintRef?.current?.click();

      history.replace({ ...location, state: { ...location.state, openPrintWindow: false } });
    }
  }, [
    labTestUrl,
    billData?.document?.createdLabTestIds?.length,
    location.state,
    location,
    defaultPrintStyles,
    history
  ]);

  usePrintShortcut(reactToPrintRef, defaultPrintStyles.applyPrintStyles);

  const [isPrinting, setIsPrinting] = React.useState(false);
  if (!billData || !billData.document) {
    return null;
  }

  return (
    <>
      <ReactToPrint
        onAfterPrint={() => {
          window.focus();
          if (addPrintCount) {
            dispatch(registerPrint(billId));
          }
          setIsPrinting(false);
        }}
        onBeforePrint={() => {
          if (onPrintClick) onPrintClick();
        }}
        trigger={() => (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <div>
            <Tooltip arrow title="Alt + P / Ctrl + P">
              <Link
                className="actionBtn"
                component="button"
                onMouseEnter={defaultPrintStyles.applyPrintStyles}
                onClick={() => {
                  if (isPrinting) return;
                  setIsPrinting(true);
                }}
                style={{ textDecoration: "none", padding: "5px 15px" }}
                ref={reactToPrintRef}
              >
                <Typography component="span" style={labelStyle}>
                  <Box className="actionBtnLabel" component="span">
                    {buttonText || "Print"}
                  </Box>
                </Typography>
              </Link>
            </Tooltip>
          </div>
        )}
        content={() => printRef.current}
      />
      <iframe title="printContents" className="displayContents">
        <div ref={printRef}>
          <Box component="table" width="100%">
            {/* thead and tfoot are repeated on every page when printing by default */}
            <tbody>
              {showLetterhead && (
                <tr>
                  <td>
                    <PrintHeader />
                  </td>
                </tr>
              )}

              <tr>
                <td>
                  <div>
                    <InfoField
                      size={Size.MEDIUM}
                      valueStyle={{
                        textAlign: "center",
                        textTransform: "uppercase",
                        padding: "0 0.1cm",
                        margin: "0.2cm 0"
                      }}
                      value={`${getPrintTitle({
                        billStatus: billData.status,
                        billType: billData.type,
                        printAsEstimate,
                        customTitle
                      })} ${
                        !printAsEstimate && !isForPreview && billData?.printCount
                          ? ` (COPY of Original ${billData?.printCount})`
                          : ""
                      }`}
                    />
                    <Box m="0cm 0.1cm" pt="0.2cm" borderTop="1px solid #bbb">
                      <div>
                        {checkWalkInCustomer(billData.client as Client, "isWalkInCustomer") && (
                          <ClientInfo
                            client={billData?.client as Client}
                            customerNumber={billData.customer?.customerNumber}
                            showDobOnPrint={showDobOnPrint}
                          />
                        )}
                        {labIdsForBill && <InfoField label="Lab Id:" value={labIdsForBill} />}
                      </div>
                      {billData.ipd && (
                        <InfoField label="IPD No." value={billData.ipd.ipdNumber || ""} />
                      )}
                      {billData.document.bed?.name && (
                        <InfoField label="Bed No." value={billData.document.bed.name} />
                      )}
                      {!printAsEstimate && (
                        <InfoField
                          label={
                            billData.type === DocumentTypes.CREDITNOTE
                              ? tl("billing.creditNoteNumber")
                              : tl("billnumber")
                          }
                          value={billData.billNumber}
                        />
                      )}
                      {billData.type === DocumentTypes.CREDITNOTE && (
                        <InfoField label="CN For Bill" value={billData.related?.billNumber} />
                      )}

                      <InfoField
                        label="Issue Date"
                        value={`${dateWithZeroBeforeSingleDigits(
                          convertADtoBS(moment(billData.issueDate)).formatted
                        )}
                        ${moment(billData.issueDate).format("h:mm a")}`}
                      />

                      <InfoField
                        label={t("billing.dateAD")}
                        value={dateWithZeroBeforeSingleDigits(
                          moment(billData.issueDate).format("YYYY/MM/DD")
                        )}
                      />
                      {billData.document.referredBy && (
                        <InfoField
                          label={t("booking.referredBy")}
                          value={billData.document.referredBy.toUpperCase()}
                        />
                      )}
                      {billData.document.extraReferrer && (
                        <InfoField
                          label="Extra Referrer"
                          value={billData.document.extraReferrer.name.toUpperCase()}
                        />
                      )}
                      {billData.ipd?.doctorInCharge && (
                        <InfoField
                          label="Consulted by"
                          value={`${billData.ipd.doctorInCharge?.firstName || ""} ${
                            billData.ipd.doctorInCharge?.lastName || ""
                          }`}
                        />
                      )}

                      {billData.insuranceNumber && (
                        <InfoField
                          label={t("billing.insuranceNumber")}
                          value={billData.insuranceNumber}
                        />
                      )}

                      {billData.claimNumber && (
                        <InfoField label={t("billing.claimNumber")} value={billData.claimNumber} />
                      )}
                      {billData?.document?.ssf?.id && (
                        <InfoField label={tl("billing.ssid")} value={billData.document.ssf.id} />
                      )}

                      {billData?.document?.ssf?.id && billData?.document?.ssf?.scheme?.value && (
                        <InfoField
                          label={tl("billing.scheme")}
                          value={`${billData.document.ssf.scheme.label} (${billData.document.ssf.scheme.value})`}
                        />
                      )}

                      {billData?.document?.ssf?.id &&
                        billData?.document?.ssf?.subProduct?.value && (
                          <InfoField
                            label={tl("billing.subScheme")}
                            value={`${billData.document.ssf.subProduct.label} (${billData.document.ssf.subProduct.value})`}
                          />
                        )}
                    </Box>
                    {/* check the version and load bill content accordingly,
                    may use switch case if third version comes  */}
                    {isOldFormatBill(billData.document.version) ? (
                      <DescribeBillV1 billData={billData} SPs={SPs} />
                    ) : (
                      <Box p="0 0.15cm" mt={0.5}>
                        <BillDescription
                          billData={billData}
                          hidePaymentMethodOnPrint={hidePaymentMethodOnPrint}
                        />
                      </Box>
                    )}
                    <Box pb="0.15cm">
                      <Barcode
                        value={billId.toString()}
                        width={1}
                        displayValue={false}
                        fontSize={3}
                        height={10}
                        font="arial"
                      />
                    </Box>
                  </div>
                </td>
              </tr>

              <tr>
                <td>
                  <Box
                    sx={{
                      paddingTop: "2mm",
                      margin: "0 0.1cm",
                      display: "flex",
                      flexDirection: "column"
                    }}
                  >
                    {/* previous due amount */}
                    {showDueOnBillPrint && clientBalance < 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "5px"
                        }}
                      >
                        <Typography sx={{ fontSize: "8px" }}>
                          {tl("billing.previousTotalDue")}:
                        </Typography>
                        <Typography sx={{ fontSize: "8px" }}>
                          {rupeeDisplay(Math.abs(clientBalance))}
                        </Typography>
                      </Box>
                    )}

                    {/* more visible remark */}
                    {hasValidRichTextRemark(billData?.document?.remarks) && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          padding: "5px 0",
                          borderBottom: "0.02mm solid #bbb",
                          gap: "5px",
                          color: "#000"
                        }}
                      >
                        <Typography sx={{ fontSize: "8px" }}>{tl("billing.remarks")}:</Typography>
                        <DataComment
                          comment={billData.document.remarks}
                          sxProp={{ fontSize: scaleFont("0.2cm", 1) }}
                        />
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      {labTestUrl && billDue <= 0 && (
                        <QrFromUrl
                          url={labTestUrl}
                          size={40}
                          label={t("billPrint.labTestQrLabel")}
                          labelFontSize="8px"
                        />
                      )}
                      <img src={logo} alt="logo" height="20mm" style={{ marginLeft: "auto" }} />
                    </Box>
                  </Box>
                </td>
              </tr>
            </tbody>
          </Box>
        </div>
      </iframe>
    </>
  );
};

export default EightyMmPrint;
